// src/plugins/sales-forecast/components/RangeSliderControl.tsx
import React from "react";
import { Range } from "react-range";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

export interface RangeSliderControlProps {
  label: string;
  min: number;
  max: number;
  step?: number;
  values: [number, number];
  onChange: (values: number[]) => void;
  formatValue?: (n: number) => string;
}

/**
 * A fully theme-aware range slider control.
 * Uses system theme (light/dark) and CSS variables for colors.
 */
export const RangeSliderControl: React.FC<RangeSliderControlProps> = ({
  label,
  min,
  max,
  step = 1,
  values,
  onChange,
  formatValue = (n) => `${n}`,
}) => {
  const theme = useSystemTheme();

  // Fetch theme colors from CSS variables
  const mutedText = "var(--muted-text-color)";
  const accentColor = "var(--accent-color)";
  const trackBg = "var(--track-bg-color)";

  // Filled (teal) segment sits between the two thumbs.
  const lo = ((values[0] - min) / (max - min)) * 100;
  const hi = ((values[1] - min) / (max - min)) * 100;

  return (
    <div
      className={`theme-${theme} transition-all duration-300`}
      style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}
    >
      {/* Single-line header: muted label + teal value range */}
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.6rem", flexWrap: "wrap" }}>
        <span
          style={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: mutedText,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
        <span style={{ fontSize: "0.7rem", fontWeight: 800, color: accentColor, whiteSpace: "nowrap" }}>
          {formatValue(values[0])} <span style={{ color: mutedText, fontWeight: 600 }}>–</span> {formatValue(values[1])}
        </span>
      </div>

      <Range
        step={step}
        min={min}
        max={max}
        values={values as number[]}
        onChange={onChange}
        renderTrack={({ props, children }) => (
          <div
            {...props}
            style={{
              ...props.style,
              height: "6px",
              borderRadius: "999px",
              background: `linear-gradient(to right, ${trackBg} 0%, ${trackBg} ${lo}%, ${accentColor} ${lo}%, ${accentColor} ${hi}%, ${trackBg} ${hi}%, ${trackBg} 100%)`,
            }}
          >
            {children}
          </div>
        )}
        renderThumb={({ props }) => (
          <div
            {...props}
            style={{
              ...props.style,
              height: "12px",
              width: "12px",
              borderRadius: "50%",
              backgroundColor: accentColor,
              border: "2px solid var(--card-bg)",
              boxShadow: `0 0 0 1px ${accentColor}`,
              transition: "background-color 0.3s, box-shadow 0.3s",
            }}
          />
        )}
      />
    </div>
  );
};